// Retry com backoff exponencial — lógica pura e testável, extraída da UI de
// upload de fotos (Etapa 11, spec S05 §Fotos: "upload em background com
// retry exponencial"). Não depende de nenhuma API nativa; quem chama passa a
// operação (`fn`) e, opcionalmente, um `sleep` e um `shouldAbort` injetáveis
// — é isso que torna o comportamento de espera/desistência testável sem
// timers reais e sem mockar rede.
//
// Decisão: `maxAttempts` default é `Infinity`. O critério de aceite da S02
// ("3 fotos em modo avião → rede volta → sobem sozinhas sozinhas") não dá
// nenhuma garantia de quanto tempo o avião fica ligado — um limite de
// tentativas fixo poderia esgotar antes da rede voltar e deixar a foto presa
// em estado de erro para sempre, o que contraria "sobem sozinhas" (sem ação
// manual da usuária). O delay é capado em `maxDelayMs` para não crescer sem
// limite. Quem chama (ver ProjectPhotosSection) cancela o loop via
// `shouldAbort` quando o componente desmonta, para não vazar retries em
// segundo plano depois que a usuária navegou para outra tela — limitação
// aceita e documentada: como o estado "enviando" vive só em memória (decisão
// da Etapa 11 sobre a exceção de outbox), desmontar o componente perde o
// progresso do upload da mesma forma que matar o app perderia.

export class RetryAbortedError extends Error {
  constructor() {
    super('retryWithBackoff: abortado (shouldAbort retornou true)');
    this.name = 'RetryAbortedError';
  }
}

export interface RetryOptions {
  /** Delay da 1ª espera, em ms (dobra a cada tentativa seguinte). Default: 1000. */
  baseDelayMs?: number;
  /** Teto do delay entre tentativas, em ms. Default: 30000. */
  maxDelayMs?: number;
  /** Número máximo de tentativas antes de desistir e propagar o último erro. Default: Infinity. */
  maxAttempts?: number;
  /** Injeção para teste — evita depender de timers reais. Default: setTimeout real. */
  sleep?: (ms: number) => Promise<void>;
  /** Checado antes de cada tentativa e antes de cada espera; se retornar
   * true, interrompe o loop lançando RetryAbortedError em vez de tentar de
   * novo. Usado para cancelar o retry quando o componente que o iniciou já
   * desmontou. */
  shouldAbort?: () => boolean;
}

/**
 * Calcula o delay (ms) antes da tentativa `attempt` (1-based: o delay é
 * aplicado DEPOIS que a tentativa `attempt` falhou, antes da `attempt + 1`).
 * Backoff exponencial simples (`base * 2^(attempt-1)`), capado em `maxDelayMs`.
 */
export function computeBackoffDelayMs(attempt: number, baseDelayMs = 1000, maxDelayMs = 30000): number {
  const delay = baseDelayMs * 2 ** Math.max(0, attempt - 1);
  return Math.min(delay, maxDelayMs);
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Executa `fn`, tentando de novo com backoff exponencial em caso de erro,
 * até `fn` resolver, até `maxAttempts` ser atingido (propaga o último erro),
 * ou até `shouldAbort()` retornar true (propaga RetryAbortedError).
 */
export async function retryWithBackoff<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const { baseDelayMs = 1000, maxDelayMs = 30000, maxAttempts = Infinity, sleep = defaultSleep, shouldAbort } = options;

  let attempt = 0;
  for (;;) {
    if (shouldAbort?.()) {
      throw new RetryAbortedError();
    }
    attempt += 1;
    try {
      return await fn();
    } catch (error) {
      if (attempt >= maxAttempts) {
        throw error;
      }
      if (shouldAbort?.()) {
        throw new RetryAbortedError();
      }
      await sleep(computeBackoffDelayMs(attempt, baseDelayMs, maxDelayMs));
    }
  }
}
